import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { Toaster } from './components/ui/sonner';
import LoginPage from './pages/LoginPage';
import UserDashboard from './pages/UserDashboard';
import AdminDashboard from './pages/AdminDashboard';
import AdminUsersPage from './pages/AdminUsersPage';
import AdminDesksPage from './pages/AdminDesksPage';
import MeetingRoomPage from './pages/MeetingRoomPage';
import ParkingPage from './pages/ParkingPage';
import PricingPage from './pages/PricingPage';
import LongPeriodRequestsPage from './pages/LongPeriodRequestsPage';
import CartPage from './pages/CartPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import { Loader2 } from 'lucide-react';
import './App.css';

// Protected Route Component
function ProtectedRoute({ children, adminOnly = false }) {
  const { user, loading, isAuthenticated, isAdmin } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (adminOnly && !isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

// Dashboard Router - decides which dashboard to show based on role
function DashboardRouter() {
  const { isAdmin } = useAuth();
  
  if (isAdmin) {
    return <AdminDashboard />;
  }
  
  return <UserDashboard />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute>
            <DashboardRouter />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/meeting-room" 
        element={
          <ProtectedRoute>
            <MeetingRoomPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/parking" 
        element={
          <ProtectedRoute>
            <ParkingPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/pricing" 
        element={
          <ProtectedRoute adminOnly={true}>
            <PricingPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/long-period-requests" 
        element={
          <ProtectedRoute adminOnly={true}>
            <LongPeriodRequestsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/users" 
        element={
          <ProtectedRoute adminOnly={true}>
            <AdminUsersPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/desks" 
        element={
          <ProtectedRoute adminOnly={true}>
            <AdminDesksPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/cart" 
        element={
          <ProtectedRoute>
            <CartPage />
          </ProtectedRoute>
        } 
      />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <LanguageProvider>
        <AuthProvider>
          <AppRoutes />
          <Toaster 
            position="top-right" 
            toastOptions={{
              className: 'rounded-xl font-medium',
            }}
          />
        </AuthProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;
